/**
 * UI Manager — фасад: initUI(), loadSeriesIntoViewport(), shared state
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [REFACTOR] Декомпозиция на 6 модулей:
 *   fileOps, seriesPanel, layoutManager, toolBindings, sidebarBindings, featureBindings
 */

import { getAllSeries, getSeriesImageIds, clearAllData, trimDataSetCache, getCachedDataSet, getDICOMTags } from '../loaders/loaderManager.js';
import { getWorkerSeries, getWorkerSeriesImageIds, hasWorkerData, clearWorkerData, getStudyOfSeries } from '../loaders/loaderWorkerBridge.js';
import { initComparePanel, onStudiesChanged } from './comparePanel.js';   // [10.7.0] раздел «Сравнение»
import { isCompareActive, getCompareViewportIds } from '../core/compareState.js';   // [10.7.0] занятые окна автоматика не трогает
import { terminatePool } from '../loaders/workerPool.js';
import { updateNativeLabel, getRenderingEngine, getViewportIds, purgeCornerstoneCache, enableMPRSync, applyMR3DPreset, loadStack, loadVolume, switchViewMode, ensureViewportsReady, ensureVolumeReady, relayoutViewports, setStackRestorer } from '../core/viewport.js';
// [10.19.5] Уровень подписей поверх снимка — правило в отдельном файле без
// импортов, чтобы проверялось тестом, а не разглядыванием экрана.
import {
  нормализовать as нормализоватьУровень,
  следующий as следующийУровень,
  классыУровня as классыУровняИнфо,
  подпись as подписьУровня,
  пояснение as пояснениеУровня,
  уровеньИзХранилища as уровеньИнфоИзХранилища,
  ВСЕ_КЛАССЫ as ВСЕ_КЛАССЫ_ИНФО,
  КЛЮЧ_ХРАНИЛИЩА as КЛЮЧ_УРОВНЯ_ИНФО,
} from '../core/overlayLevel.js';
import { getCinematicRenderer, isMprLayout, задатьИсточникТекущегоОкна } from '../core/viewportState.js';
import { initGridState, setGridLayer, isGridLayer, refreshGridOverlays } from '../core/gridOverlay.js';
import { cornerstone } from '../core/init.js';
import { initGlobalSliceSlider } from './globalSliceSlider.js';
import { initProtocolCheck } from './protocolCheck.js';
import { initTools, setActiveTool, getActiveTool, clearAnnotations, activateDataTools, getAnnotations, exportAnnotations, привязатьМаскуКОкнам, isDisplayToolEnabled, toggleDisplayTool } from '../tools/toolManager.js';  // [7.0.46 FIX] getAnnotations/exportAnnotations добавлены — без них queueMeasurementsSync/queueAnnotationsSync падали ReferenceError
import { showToast, formatPatientName } from '../utils/helpers.js';
import { getGPUInfo } from '../core/gpuDetector.js';
import { initStudyTools, forceSaveAnnotations, generateThumbnailForCurrent, highlightActiveSeries, autoLoadAnnotations } from './studyToolsUI.js';
import { emit, on as busOn, clearHistory } from '../core/eventBus.js';
import { registerAPIs } from '../core/viewerAPI.js';

// ── Extracted modules ──
import { initFileOps, handleFiles, getLastLoadedFiles, setLastLoadedFiles, isWorkerPath, setUseWorkerPath, cancelLoad, openFolderPicker} from './fileOps.js';
import { planStackFill } from './stackFillPlan.js';   // [10.0.2] кому какая серия в многооконной раскладке
import { initSeriesPanel, renderSeriesList, autoSelectTool, autoPresetByModality, updateOverlays, updateWLOverlay } from './seriesPanel.js';
import { погаситьПресетыБезИсследования } from './presetModality.js';   // [12.0.34], [12.0.35]
import { панельБезИсследования } from './modalityPanel.js';   // [12.0.55]
import { сброситьСведения } from './imageInfo.js';   // [12.0.25] раздел «Информация»: все шесть строк
import { initLayoutManager, toggleMaximizeViewport, mobileMPRFocus, mobileMPRRestore, setupDefaultViewport, activateViewMode, showPreset3DFullscreen, changeLayout } from './layoutManager.js';
import { initToolBindings, undoAnnotation, redoAnnotation, initHotkeyActions } from './toolBindings.js';
import { initSidebarBindings, openModal, closeModal } from './sidebarBindings.js';
import { initFeatureBindings } from './featureBindings.js';
import { initContextUI, контекстныеПодсказки } from './contextUI.js';
import { initAdvancedUI } from './advancedUI.js';
import { initDICOMwebUI } from './dicomWebUI.js';
import { initExportModule } from './exportModule.js';
import { log } from '../utils/logger.js';
import { memoryGuardian } from '../managers/MemoryGuardian.js';   // [10.0.7] упреждающий сброс при смене серии
// [10.19.32] Список сокращён вслед за экспортами: восьмёрка (кальций/патология/
// статистика — решение 10.3.5; полёты к нулю; ложный блюр; голос-троевластие;
// радиал-меню-призрак) удалена из newFeaturesBindings, а этот импортёр при
// первой правке был ПРОПУЩЕН — несуществующий именованный импорт валил бы
// uiManager, то есть весь интерфейс. Поймано вопросом владельца «есть ошибки?»
// и его же tools/check_imports. Урок вшит: правишь экспорты — обходи ВСЕХ.
// [11.1.10] «syncAllViewports» ИЗ СПИСКА УБРАН — ловушка сработала в точности
// как описано строками выше. В [11.1.1] обёртку в newFeaturesBindings развели
// с ядром по имени (теперь она «переключитьСвязкуОкон»), внутри модуля все
// упоминания поправили, а этого импортёра снова пропустили. Несуществующий
// именованный импорт — ошибка СВЯЗЫВАНИЯ: браузер не исполняет ни строки из
// main.js, и умирает весь интерфейс, включая режим сравнения с его полоской
// «Связать здесь / Сброс / Измерить очаг / Карта разницы». Найдено
// tools/link_check.mjs. Ни одно из имён списка здесь не ИСПОЛЬЗУЕТСЯ — кнопки
// привязывает сам модуль (initNewFeaturesButtons, зовётся из index.html), — но
// ввоз оставлен: он документирует связь и грузит модуль в общий граф.
// [12.0.52] detectAorticAneurysm, autoDetectCriticalFindings, analyzeBrainAtrophy удалены из программы.
import { computeEF, segmentOrgansByDensity, exportAnimatedGIF, startScreenRecording, stopScreenRecording, enableFlashlight, disableFlashlight, showLetterbox, hideLetterbox, createOrientationCube, startTourRecording, stopTourRecording, enableRRTimeline, enableAllGestures, startMacroRecording, stopMacroRecording } from './newFeaturesBindings.js';

window.__eventBus = { on: busOn, emit: emit };

// [6.7.1] Мобильный старт (≤768px): единственное окно — нативная 2D-стопка первичных
// срезов. MPR и отдельные проекции (Axial/Sagittal/Coronal/3D) — по кнопкам верхней
// панели. Десктоп по-прежнему открывается сразу в MPR-раскладке 2×2.
const _isMobileStart = (typeof window !== 'undefined') && window.innerWidth <= 768;
let _currentLayout = _isMobileStart ? '1x1' : 'mpr';
let _currentMode = _isMobileStart ? '2d' : 'mpr';
let _currentSeries = null;
let _currentViewportId = null;
let _currentImageIds = null;
let _currentVolumeId = null;
let _cineDatasetId = null;
let _cinematicHandle = null; // [7.0.46 FIX] был НЕ объявлен → getCinematicHandle/setCinematicHandle падали ReferenceError (строгий режим)
let _sliceStep = 1; // 1=все, 2=каждый 2-й, 3=каждый 3-й, 4, 5
let _loadingLock = false; // [FIX] Защита от параллельных загрузок

// ============================================================
// [FIX-P1] Единая карта VRT пресетов (устранена 3× дупликация)
// ============================================================
const VRT_PRESET_MAP = {
  'ct-bone-3d':  { cs3d: 'ct-bone-clean',       ww: 2000, wc: 300  },
  'ct-skin':     { cs3d: 'ct-skin',              ww: 400,  wc: 40   },
  'ct-muscle':   { cs3d: 'ct-muscle',            ww: 800,  wc: 100  },
  'ct-vascular': { cs3d: 'ct-vascular',          ww: 700,  wc: 200  },
  'ct-air':      { cs3d: 'ct-airways',           ww: 1500, wc: -600 },
  'ct-lung-3d':  { cs3d: 'ct-lung-parenchyma',   ww: 1500, wc: -600 },
  'ct-colon':    { cs3d: 'ct-colon',             ww: 400,  wc: 40   },
  'mri-brain-3d':{ cs3d: 'mri-brain',            ww: 80,   wc: 40   },
  'mri-wm-gm':  { cs3d: 'mri-wm-gm',           ww: 500,  wc: 200  },
  'cardiac-3d':  { cs3d: 'cardiac-3d',           ww: 700,  wc: 200  },
  'angio-3d':    { cs3d: 'angio-3d',             ww: 700,  wc: 300  },
  'fetus':       { cs3d: 'fetus',                ww: 500,  wc: 250  },
};

// ============================================================
// HELPERS
// ============================================================
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);
const on = (id, evt, fn) => { const el = $(id); if (el) el.addEventListener(evt, fn); };
const onAll = (sel, evt, fn) => $$(sel).forEach(el => el.addEventListener(evt, fn));

// [FIX-6] Универсальный геттер серий — ищет в обоих источниках
function _getAllSeries() {
  if (isWorkerPath() && hasWorkerData()) return getWorkerSeries();
  const s = getAllSeries();
  return Array.isArray(s) ? s : Object.values(s);
}
function _getSeriesImageIds(seriesUID) {
  if (isWorkerPath()) {
    const ids = getWorkerSeriesImageIds(seriesUID);
    if (ids.length) return ids;
  }
  return getSeriesImageIds(seriesUID);
}

// ══════════════════════════════════════════════════════════════
// [FIX-CS3Dv2] Универсальный доступ к скалярным данным объёма
// CS3D v2 убрал volume.getScalarData() и imageData.scalarData
// Три уровня fallback: VTK actors → voxelManager → legacy
// ══════════════════════════════════════════════════════════════
function _getVolumeScalarData() {
  let data = null, dims = null, spacing = null, origin = null;

  // ── Путь 1: VTK actors → mapper → imageData → scalars.getData() ──
  try {
    const re = getRenderingEngine();
    if (re) {
      for (const vpId of getViewportIds()) {
        if (data) break;
        const vp = re.getViewport(vpId);
        if (!vp) continue;
        for (const ae of (vp.getActors?.() || [])) {
          const id = ae?.actor?.getMapper?.()?.getInputData?.();
          if (!id) continue;
          const sc = id.getPointData?.()?.getScalars?.();
          if (sc && typeof sc.getData === 'function') {
            data = sc.getData();
            dims = id.getDimensions();
            spacing = id.getSpacing();
            origin = id.getOrigin();
            break;
          }
        }
      }
    }
  } catch (e) { log.warn('[VolumeData] VTK path:', e.message); }

  // ── Путь 2: volume cache → voxelManager (CS3D v2) ──
  if (!data && _currentVolumeId) {
    try {
      const cs = window.cornerstone;
      const vid = _currentVolumeId.startsWith('cornerstoneStreamingImageVolume:')
        ? _currentVolumeId : `cornerstoneStreamingImageVolume:${_currentVolumeId}`;
      const vol = cs?.cache?.getVolume?.(vid);
      if (vol) {
        // CS3D v2: основной метод
        try { data = vol.voxelManager?.getCompleteScalarDataArray?.(); } catch (_) { log.debug('[UiManager]', _.message); }
        // Fallback: VTK imageData из volume
        if (!data && vol.imageData) {
          try { data = vol.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (_) { log.debug('[UiManager]', _.message); }
        }
        if (data) { dims = vol.dimensions; spacing = vol.spacing; origin = vol.origin; }
      }
    } catch (e) { log.warn('[VolumeData] voxelManager:', e.message); }
  }

  // ── Путь 3: legacy getScalarData() (CS3D v1) ──
  if (!data && _currentVolumeId) {
    try {
      const cs = window.cornerstone;
      const vid = _currentVolumeId.startsWith('cornerstoneStreamingImageVolume:')
        ? _currentVolumeId : `cornerstoneStreamingImageVolume:${_currentVolumeId}`;
      const vol = cs?.cache?.getVolume?.(vid);
      if (vol) { data = vol.getScalarData?.(); if (data) { dims = vol.dimensions; spacing = vol.spacing; origin = vol.origin; } }
    } catch (_) { log.debug('[UI] expected CS3D v2 warning'); }
  }

  if (!data || !dims) return null;
  return { scalarData: data, dimensions: dims, spacing, origin };
}

// ============================================================
// INIT — called from main.js
// ============================================================
export function initUI() {
  // [11.1.53] Восстановитель стековых окон регистрируется ОДИН раз, здесь.
  // setupViewports пересоздаёт окна (re.setViewports), после чего нативное
  // окно пусто по построению; вернуть в него стопку мог только changeLayout —
  // один из четырёх вызывающих. Теперь это делается в самом setupViewports
  // через эту функцию, и знание «какая серия чья» остаётся в uiManager.
  setStackRestorer(_восстановитьСтопки);
  // [12.0.49] Окно W/L из правой панели ложится на окно, выбранное щелчком, и
  // на окна той же модальности — ядру нужно знать, какое окно выбрано.
  задатьИсточникТекущегоОкна(() => _currentViewportId);
  // ── [REFACTOR] Initialize extracted modules with shared context ──
  // ── Shared context getters ──
  const sharedCtx = {
    getCurrentMode: () => _currentMode,
    getCurrentLayout: () => _currentLayout,
    getCurrentViewportId: () => _currentViewportId,
    setCurrentViewportId: (v) => { _currentViewportId = v; },
    getCurrentSeries: () => _currentSeries,
    getCurrentImageIds: () => _currentImageIds,
    getCurrentVolumeId: () => _currentVolumeId,
    // [10.0.4] Нужен layoutManager'у: он строит объём по требованию, когда
    // человек открывает проекцию, а объёма ещё нет.
    setCurrentVolumeId: (v) => { _currentVolumeId = v; },
    getSliceStep: () => _sliceStep,
    setSliceStep: (v) => { _sliceStep = v; },
    getCineDatasetId: () => _cineDatasetId,
    getCinematicHandle: () => _cinematicHandle,
    setCinematicHandle: (v) => { _cinematicHandle = v; },
    getAllSeries: () => _getAllSeries(),
    // [6.7.12] Раньше отсутствовал в sharedCtx → в отчётах (exportModule) пациент/модальность
    // выходили «—». Отдаём здесь, чтобы отчёты подхватывали шапку исследования.
    getCurrentStudyMeta: () => {
      const series = _getAllSeries();
      const first = series[0] || {};
      return {
        patientName: (formatPatientName?.(first.patientName || '') || first.patientName || ''),
        studyDate: first.studyDate || '',
        modality: series.map(s => s.modality).filter((v, i, a) => v && a.indexOf(v) === i).join('/'),
        seriesCount: series.length,
      };
    },
    getVolumeScalarData: () => _getVolumeScalarData(),
    VRT_PRESET_MAP,
    openModal,
    closeModal,
    loadSeriesIntoViewport,
    // [10.7.0] Список серий перерисовали — значит набор исследований мог
    // измениться: загрузили второе, или третье вытеснило самое давнее. Раздел
    // «Сравнение» узнаёт об этом отсюда, а не собственным опросом по таймеру.
    renderSeriesList: (list) => {
      renderSeriesList(list);
      try { onStudiesChanged(); } catch (e) { log.debug('[Сравнение] обновление списка:', e.message); }
    },
    showLoadingOverlay,
    updateLoadingOverlay,
    hideLoadingOverlay,
    forceSaveAnnotations,
    renderDICOMTags,
    clearAllData: () => {
      clearAllData(); clearWorkerData(); terminatePool(); clearAnnotations();
      // [12.0.57] Было по-английски «Load DICOM files» — после «Очистить» список серий заговаривал на чужом языке. Текст — как в разметке при запуске.
      const sl = $('series-list'); if (sl) sl.innerHTML = '<div class="series-empty">Загрузите DICOM для отображения серий</div>';
      setLastLoadedFiles([]); setUseWorkerPath(false);
      _currentSeries = null; _currentImageIds = null; _currentVolumeId = null;
      purgeCornerstoneCache(); renderDICOMTags(); clearHistory();

      // [11.0.6] ВОЗВРАТ В ПУСТОЕ СОСТОЯНИЕ, а не только очистка памяти.
      //
      // Прежде очистка убирала ДАННЫЕ и на этом заканчивалась: четыре окна
      // раскладки продолжали показывать последний кадр, подписи над снимком
      // держали имя пациента, а стартовая заставка не возвращалась. Со стороны
      // выглядело так, будто ничего не произошло, — о чём владелец и сказал.
      // Данных при этом в памяти уже не было: на экране стояла картинка,
      // за которой ничего не стоит. Это хуже, чем не очистить вовсе.
      // [12.0.14] Наложения гасим ДО очистки окон. Из журнала владельца:
      // включённая сетка срезов пережила «Очистить всё», и на первой же
      // перерисовке её отрисовщик спросил у опустевшего окна список кадров —
      // getImageIds у объёмного окна без актёра БРОСАЕТ исключение
      // («No actor found for the given volumeId: undefined»), и оно валилось
      // наружу необработанным: зовётся из цикла отрисовки библиотеки, а не из
      // нашего кода. Инструмент, переживший свои данные, ломает отрисовку окна.
      try {
        for (const имя of ['OverlayGrid', 'ReferenceLines']) {
          if (isDisplayToolEnabled(имя)) toggleDisplayTool(имя);
        }
      } catch (e) { log.debug('[Очистка] наложения:', e.message); }

      try {
        const re = getRenderingEngine();
        if (re) {
          for (const vp of (re.getViewports?.() || [])) {
            // Снимаем содержимое, а не окно целиком: раскладка должна
            // остаться на месте — врач сейчас откроет следующее исследование.
            try { vp.setStack?.([], 0); } catch (e) { /* объёмному окну стопку не поставить */ }
            try { vp.removeAllActors?.(); } catch (e) { /* у стопки актёров нет */ }
            try { vp.resetCamera?.(); vp.render?.(); } catch (e) { /* окно уже снято */ }
          }
        }
      } catch (e) { log.debug('[Очистка] окна:', e.message); }

      // Подписи над снимком: без этого над пустым окном оставалось имя
      // пациента из закрытого исследования — прямая дорога перепутать больных.
      try {
        for (const ид of ['overlay-patient', 'overlay-study', 'overlay-series', 'overlay-image',
          'overlay-modality', 'overlay-size', 'overlay-date', 'overlay-demo', 'overlay-dobtime']) {
          const el = $(ид); if (el) el.textContent = '';
        }
        const wl = $('overlay-wl'); if (wl) wl.textContent = 'W: — / L: —';
        // [12.0.25] Раздел «Информация» — все шесть строк и с подписями.
        // Здесь стояли четыре id и голый «—»: подпись «Пациент:» пропадала,
        // а «Шаг» и «Матрица» оставались от закрытого исследования.
        сброситьСведения();
        // [12.0.35] Исследование закрыто — пресеты серые и неактивные: без
        // снимка им не к чему применяться. В 12.0.34 здесь все пресеты
        // возвращались в строй — это было неверно.
        погаситьПресетыБезИсследования();
        // [12.0.55] Исследование закрыто — правая панель снова целиком: без
        // снимка модальности нет, и прятать по ней нечего.
        панельБезИсследования();
        const hu = $('hu-value-display'); if (hu) hu.textContent = 'HU: —';
      } catch (e) { log.debug('[Очистка] подписи:', e.message); }

      // Заставка возвращается — программа снова предлагает открыть папку.
      // Правило снятия заставки (welcomeGate) само по себе её не вернёт: оно
      // решает, когда СНИМАТЬ, а не когда показывать снова.
      // [12.0.51] Гашение миникарты убрано вместе с ней самой (пустой квадрат,
      // холст не рисовал никто — надгробие в ui/sidebarBindings.js).

      // Бегунок срезов: его правило видимости пересчитывается только по
      // событию среза, а при очистке событий нет — без этого вызова он остался
      // бы висеть поверх пустой заставки. Ровно это владелец и увидел.
      import('./globalSliceSlider.js')
        .then((м) => м.забытьПозицииБегунка?.())
        .catch((e) => log.debug('[Очистка] бегунок:', e.message));

      // Модуль заставки подгружается по требованию — как и в main.js: держать
      // его в постоянных зависимостях ради одной кнопки ни к чему.
      import('./premiumFeatures.js')
        .then((м) => м.вернутьЗаставку?.())
        .catch((e) => log.debug('[Очистка] заставка:', e.message));

      showToast('Исследования закрыты — программа пуста', 'info');
    },
  };

  initSeriesPanel({
    ...sharedCtx,
  });

  initLayoutManager({
    ...sharedCtx,
    // [10.0.2] Смена раскладки пересобирает окна с нуля, поэтому привязка
    // «окно → серия» обнуляется здесь, а не по сравнению имён: переключение
    // 2×2 → 2×2 (человек нажал ту же кнопку) тоже создаёт новые окна, а имена
    // у них прежние — по именам такой случай не отличить, и три окна из
    // четырёх остались бы «занятыми» и потому пустыми.
    setCurrentLayout: (v) => { _currentLayout = v; _resetViewportSeriesMap(v); },
    setCurrentMode: (v) => { _currentMode = v; },
  });

  initFileOps(sharedCtx);

  // [FIX] DICOMweb Browser — PACS подключение через QIDO-RS/WADO-RS
  initDICOMwebUI(sharedCtx);

  // [NEW] Export Module — JPEG/Video/PDF с аннотациями
  initExportModule(sharedCtx);

  initToolBindings(sharedCtx);

  // [6.0.0] Общий ползунок синхронного листания всех окон
  initGlobalSliceSlider();

  // [6.0.5] Протокол-контроль: сверка исследования с врачебным чек-листом
  initProtocolCheck({ getAllSeries: _getAllSeries });

  initSidebarBindings(sharedCtx);

  // [10.7.0] Раздел «Сравнение» — два места под два исследования.
  initComparePanel();

  initFeatureBindings(sharedCtx);

  // [NEW] Контекстный UI — показывает только релевантные инструменты по модальности
  initContextUI(sharedCtx);

  // [NEW] Advanced UI — Command Palette, Radial Menu, Ambient Glow, Smart Toolbar
  initAdvancedUI(sharedCtx);

  // ── Remaining bindings (uiManager-only) ──
  bindViewportDragDrop();
  bindViewportMouseTracking();
  initHotkeyActions();
  setupDefaultViewport().catch(e => log.warn('[UI] Default viewport:', e));
  updateGPUStatusBar();

  // Инициализация ZIP/Аннотации/Thumbnails
  initStudyTools({
    getLastLoadedFiles: () => getLastLoadedFiles(),
    getAllSeries: () => _getAllSeries(),
    // [11.0.2] Срезы серии — источник превью, не зависящий от того, открывали
    // её или нет. Раньше миниатюра снималась с канвы вьюпорта, то есть была
    // только у уже открытой серии — а список нужен как раз для того, чтобы
    // решить, какую открывать.
    getSeriesImageIds: (uid) => _getSeriesImageIds(uid),
    getCurrentSeries: () => _currentSeries,
    getCurrentStudyMeta: () => {
      const series = _getAllSeries();
      const first = series[0] || {};
      return {
        patientName: first.patientName || 'Unknown',
        studyDate: first.studyDate || '',
        modality: series.map(s => s.modality).filter((v,i,a) => a.indexOf(v)===i).join('/'),
        seriesCount: series.length,
      };
    },
    handleFiles: (f) => handleFiles(f),
    showLoadingOverlay,
    updateLoadingOverlay,
    hideLoadingOverlay,
    loadSeriesIntoViewport,
  });

  // ── [5.0.14] Стопка 1:1 — родной срез, пиксель снимка = пиксель экрана ──
  const btn1to1 = document.getElementById('btn-view-1to1');
  if (btn1to1) btn1to1.addEventListener('click', () => {
    const fit = () => {
      try {
        const re = getRenderingEngine();
        const ids = getViewportIds() || [];
        const vpId = ids.find(id => re.getViewport(id)?.type === 'stack') || ids[0];
        const vp = re.getViewport(vpId);
        if (!vp || vp.type !== 'stack') { showToast('1:1 доступен в режиме «2D Стопка»', 'warning', 2500); return; }
        const d = vp.getImageData?.();
        const sp = d?.spacing?.[1] || d?.spacing?.[0] || 1;
        vp.setCamera({ parallelScale: (vp.element.clientHeight * sp) / 2 });
        vp.render();
        showToast('🔍 1:1 — родной срез без пересчёта', 'info', 2200);
      } catch (e) { log.warn('[1:1]', e.message); }
    };
    const btn2d = document.getElementById('btn-mode-2d');
    if (btn2d && !btn2d.classList.contains('active')) {
      btn2d.click();               // сперва в родную стопку
      setTimeout(fit, 700);        // после загрузки стопки
      setTimeout(fit, 1600);       // страховка на медленной серии
    } else fit();
  });

  // ── [10.19.5] Текст поверх снимка: подробно → кратко → скрыто ──
  //
  // Было два состояния, и ни одно не убирало подписи со снимка совсем — а
  // попросили именно это: столбец занимал четверть аксиального окна. Правило
  // (порядок обхода, состав «кратко», перевод старой настройки) живёт в
  // core/overlayLevel.js под тестом; здесь только применение к странице.
  const btnRich = document.getElementById('btn-overlay-rich');
  let _уровеньИнфо = 'кратко';
  const _применитьУровень = (уровень, запомнить = true) => {
    _уровеньИнфо = нормализоватьУровень(уровень);
    document.body.classList.remove(...ВСЕ_КЛАССЫ_ИНФО);
    document.body.classList.add(...классыУровняИнфо(_уровеньИнфо));
    if (btnRich) {
      const подпись = document.getElementById('btn-overlay-rich-label');
      if (подпись) подпись.textContent = подписьУровня(_уровеньИнфо);
      btnRich.title = пояснениеУровня(_уровеньИнфо);
      // Подсвечена, пока подписи вообще есть: «скрыто» — состояние покоя.
      btnRich.classList.toggle('active', _уровеньИнфо !== 'скрыто');
      btnRich.dataset.level = _уровеньИнфо;
    }
    if (запомнить) {
      try { localStorage.setItem(КЛЮЧ_УРОВНЯ_ИНФО, _уровеньИнфо); } catch (e) { /* приватный режим */ }
    }
  };
  const _обойтиУровень = () => {
    _применитьУровень(следующийУровень(_уровеньИнфо));
    try {
      showToast(`${подписьУровня(_уровеньИнфо)}${_уровеньИнфо === 'скрыто' ? ' — снимок без подписей' : ''}`,
        'info', 1800);
    } catch (e) { /* */ }
  };
  if (btnRich) btnRich.addEventListener('click', _обойтиУровень);
  registerAPIs({ overlayLevel: () => _уровеньИнфо, setOverlayLevel: (у) => _применитьУровень(у), cycleOverlayLevel: _обойтиУровень });
  // [10.19.7] Горячая клавиша — H (русская «Р»), «hide/спрятать».
  //
  // Сначала я поставил сюда I — «инфо», — и это была ошибка: I УЖЕ занят
  // инверсией, причём в трёх местах (hotkeys.js, viewportKeyboard.js и
  // точечный обработчик в appInit.js). Нажатие делало сразу два дела: меняло
  // подписи и переворачивало яркость снимка. Заметил врач, а не проверка, —
  // поэтому вместе с этой правкой появился hotkeys_test.mjs: он разбирает
  // исходники и падает, если одна клавиша делает два дела.
  //
  // H выбрана по разбору всех карт: свободна и там, и в инструментах.
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (e.key === 'h' || e.key === 'H' || e.key === 'р' || e.key === 'Р') { e.preventDefault(); _обойтиУровень(); }
  });
  try { _применитьУровень(уровеньИнфоИзХранилища((k) => localStorage.getItem(k)), false); }
  catch (e) { _применитьУровень('кратко', false); }

  // ── [5.0.17] «Окно как в кадре» — W/L из тегов текущего кадра ──
  const btnPrintWL = document.getElementById('btn-wl-asprinted');
  if (btnPrintWL) btnPrintWL.addEventListener('click', () => {
    try {
      const re = getRenderingEngine();
      const ids = getViewportIds() || [];
      const vpStack = ids.map(id => re.getViewport(id)).find(v => v?.type === 'stack');
      let imageId = vpStack?.getCurrentImageId?.() || null;
      if (!imageId && _currentImageIds?.length) imageId = _currentImageIds[Math.floor(_currentImageIds.length / 2)];
      const ds = imageId ? getCachedDataSet(imageId) : null;
      const wc = parseFloat(ds?.floatString?.('x00281050', 0));
      const ww = parseFloat(ds?.floatString?.('x00281051', 0));
      if (!(Number.isFinite(wc) && Number.isFinite(ww) && ww > 0)) { showToast('В тегах кадра нет окна (0028,1050/1051)', 'warning', 2600); return; }
      const lower = wc - ww / 2, upper = wc + ww / 2;
      for (const id2 of ids) {
        const v2 = re.getViewport(id2);
        if (!v2 || String(v2.type || '').toLowerCase().includes('3d')) continue;
        v2.setProperties?.({ voiRange: { lower, upper } });
        v2.render?.();
      }
      updateWLOverlay(Math.round(ww), Math.round(wc));
      showToast(`🖨 Окно кадра: W ${Math.round(ww)} / L ${Math.round(wc)}`, 'info', 2200);
    } catch (e) { log.warn('[WL/кадр]', e.message); }
  });

  // ── [5.0.28] Контрольные метки и сетка: три независимых тумблера, все вкл. по умолчанию ──
  initGridState();
  // [6.0.3] Диагностика: если все слои разметки выключены (сохранённое состояние
  // тумблеров) — сообщаем, чтобы «пропавшая сетка» не выглядела поломкой
  if (!isGridLayer('grid') && !isGridLayer('scaleV') && !isGridLayer('scaleH')) {
    showToast('Шкалы и сетка выключены тумблерами «Метки/Сетка» — включите их в панели', 'warning', 5000);
  }
  for (const [btnId, layer] of [['btn-mark-v', 'scaleV'], ['btn-mark-h', 'scaleH'], ['btn-grid-full', 'grid']]) {
    const b = document.getElementById(btnId);
    if (!b) continue;
    const sync = () => b.classList.toggle('active', isGridLayer(layer));
    b.addEventListener('click', () => { setGridLayer(layer, !isGridLayer(layer)); sync(); });
    sync();
  }

  // ── [5.0.27] МР-пресеты 3D ──
  document.querySelectorAll('[data-mr3d]').forEach((b) => b.addEventListener('click', () => {
    if (applyMR3DPreset(b.dataset.mr3d)) {
      document.querySelectorAll('[data-mr3d]').forEach((x) => x.classList.toggle('active', x === b));
    }
  }));

  log.debug('[UI] All handlers bound');
}

// ══════════════════════════════════════════════════════════════
//  Нативное окно: с [12.0.10] следует за выбранной серией
// ══════════════════════════════════════════════════════════════
// [12.0.10] Надгробие: здесь жили _isDerivedSeries и _pickNativeSeriesUID —
// правило [NATIVE 6.0.0] «самая тонкосрезная ORIGINAL-серия исследования,
// независимая от выбора врача». Владелец его отменил: нативные срезы есть у
// КАЖДОЙ серии — это её собственные кадры, — и при смене серии нативное окно
// должно показывать кадры выбранной. Обе функции удалены вместе с правилом;
// ручной выбор другой серии остаётся — выпадающим списком в углу нативного
// окна (_renderNativeSeriesSelect).

/**
 * [11.1.53] ВЕРНУТЬ СТОПКИ В ОКНА ПОСЛЕ ИХ ПЕРЕСОЗДАНИЯ.
 *
 * Зовётся из setupViewports сразу после re.setViewports — то есть в момент,
 * когда все окна созданы заново и все стековые пусты. Вызывающих у
 * setupViewports четыре (changeLayout, applyHangingProtocol,
 * setupDefaultViewport, viewportSync), и стопку возвращал только первый.
 * Отсюда «Scroll::Stack Viewport has no images» на каждый щелчок колеса.
 *
 * ЧТО ДЕЛАЕТ. Если исследование не открыто — молчит, восстанавливать нечего.
 * Иначе кладёт нативную серию в окно «native» и текущую серию в одиночное
 * стековое окно. Многооконные стековые раскладки (1×2, 2×2) здесь НЕ
 * заполняем: у них свой хозяин — _заполнитьСтековыеОкна, со своим планом и
 * памятью о том, что врач положил руками; дублировать его логику значило бы
 * завести второго хозяина и затирать выбор человека.
 *
 * @param {string[]} viewportIds — окна, которые только что созданы
 * @param {string} layout — раскладка, под которую они созданы
 */
async function _восстановитьСтопки(viewportIds, layout) {
  if (!_currentSeries) { log.debug('[UI] стопки не восстанавливаю: исследование не открыто'); return; }
  const ids = Array.isArray(viewportIds) ? viewportIds : [];
  let вернул = 0;

  // Нативное окно: серия, выбранная врачом, — с [12.0.10] оно следует за
  // MPR-серией (см. блок смены серии), и после пересоздания окон в него
  // возвращаются ЕЁ кадры.
  if (ids.includes('native')) {
    try {
      const nativeIds = _currentImageIds || _getSeriesImageIds(_currentSeries);
      if (nativeIds?.length) {
        await loadStack('native', nativeIds);
        updateNativeLabel(nativeIds);
        _renderNativeSeriesSelect(_currentSeries);
        вернул++;
      } else {
        log.warn('[UI] нативное окно: кадров для восстановления нет');
      }
    } catch (e) { log.warn('[UI] нативное окно после пересоздания:', e.message); }
  }

  // Одиночное стековое окно раскладки 1×1.
  if (!isMprLayout(layout) && ids.length === 1) {
    const кадры = _currentImageIds || _getSeriesImageIds(_currentSeries);
    if (кадры?.length) {
      try { await loadStack(ids[0], кадры); вернул++; }
      catch (e) { log.warn(`[UI] окно ${ids[0]} после пересоздания:`, e.message); }
    }
  }

  // [11.1.64] Показ МАСКИ теряется при пересоздании окон так же, как стопка:
  // addLabelmapRepresentationToViewport зовётся только при создании холста.
  // Владелец на этом потерял вид разметки дважды — при переходе в mpr6 и при
  // возврате в mpr, — хотя сама маска была цела (экспорт NIfTI дал 409 МиБ).
  // Возвращаем здесь же, одним хозяином со стопками.
  let маскаВОкнах = 0;
  try { маскаВОкнах = await привязатьМаскуКОкнам(ids.filter((id) => id !== 'volume3d' && id !== 'view-3d')); }
  catch (e) { log.warn('[UI] показ маски после пересоздания:', e.message); }

  log.info(`[UI] после пересоздания окон (${layout}) стопок возвращено: ${вернул}, показ маски в ${маскаВОкнах} окнах`);
  if (вернул) { try { activateDataTools(); } catch (e) { /* инструменты добьёт setupViewports */ } }
}

/** Выпадающий выбор серии в углу нативного окна (пересоздаётся при смене раскладки) */
function _renderNativeSeriesSelect(selectedUID) {
  try {
    const vpEl = document.getElementById('viewport-native');
    if (!vpEl) return;
    let sel = vpEl.querySelector(':scope > .native-series-select');
    if (!sel) {
      sel = document.createElement('select');
      sel.className = 'native-series-select';
      sel.title = 'Серия нативного окна';
      ['pointerdown', 'mousedown', 'touchstart', 'wheel'].forEach(ev =>
        sel.addEventListener(ev, e => e.stopPropagation()));
      sel.addEventListener('change', async () => {
        const ids = _getSeriesImageIds(sel.value);
        if (!ids?.length) return;
        try { await loadStack('native', ids); updateNativeLabel(ids); activateDataTools(); }
        catch (e) { log.warn('[NATIVE] смена серии:', e.message); }
      });
      vpEl.appendChild(sel);
    }
    const all = _getAllSeries();
    sel.innerHTML = all.map(s => {
      const n = (s.images || s.files || []).length;
      const d = String(s.seriesDesc || s.modality || 'серия').slice(0, 30);
      return `<option value="${s.seriesUID}"${s.seriesUID === selectedUID ? ' selected' : ''}>${d} · ${n} срез.</option>`;
    }).join('');
  } catch (e) { log.debug('[NATIVE] select:', e.message); }
}

// ══════════════════════════════════════════════════════════════
// [10.0.2] Многооконные раскладки стопок (1×2, 2×2 и прочие сетки)
// ══════════════════════════════════════════════════════════════

/**
 * Какая серия лежит в каком окне. Нужно, чтобы не затирать то, что человек
 * положил туда руками: выбрал окно, открыл в нём другую серию — при следующей
 * загрузке оно должно остаться как есть.
 */
const _vpSeries = new Map();
let _vpSeriesLayout = null;

/** Раскладку пересобрали — прежние привязки недействительны. */
function _resetViewportSeriesMap(layout) {
  _vpSeries.clear();
  _vpSeriesLayout = layout;
}

/**
 * [10.0.2] Заполнить ВСЕ окна раскладки, а не одно.
 *
 * Что было. `loadStack(_currentViewportId, imageIds)` — ровно одно окно.
 * В раскладках 1×2 и 2×2 остальные оставались чёрными навсегда: заполнить их
 * можно было только вручную — нажать на пустое окно и выбрать серию в списке.
 * Про это нигде не было сказано, и раскладка выглядела нерабочей: человек
 * переключался на «четыре окна» и получал одно изображение и три чёрных
 * прямоугольника.
 *
 * Что теперь. Активное окно получает выбранную серию, остальные — другие
 * серии исследования по порядку. Если серий меньше, чем окон, оставшиеся
 * получают ту же самую: одинаковая картинка рядом — это осмысленно (можно
 * поставить разные окна плотности или разные срезы), а чёрный прямоугольник —
 * нет.
 *
 * Окна, куда серию клали вручную, не трогаются.
 */
async function _fillStackViewports(seriesUID, imageIds) {
  const vpIds = getViewportIds();
  if (!vpIds.length) { await loadStack(_currentViewportId, imageIds); return; }

  // [10.7.0] Идёт сравнение двух исследований — оба окна заняты тем, что врач
  // положил туда сам. Раскладывать по ним «остальные серии исследования»
  // нельзя: это стёрло бы сравнение, ничего не объяснив. Нажатие на серию в
  // это время открывает её в АКТИВНОМ окне и только в нём.
  if (isCompareActive()) {
    // Сравнение ещё расставляет окна (список пуст) — не мешаем ему вовсе:
    // иначе загрузим стопку, которую через миг сами же и заменим.
    if (!getCompareViewportIds().length) return;
    await loadStack(_currentViewportId, imageIds);
    _vpSeries.set(_currentViewportId, seriesUID);
    return;
  }

  if (_vpSeriesLayout !== _currentLayout) _resetViewportSeriesMap(_currentLayout);

  // [10.7.0] Соседние окна заполняем сериями ТОГО ЖЕ исследования.
  //
  // С тех пор как программа держит два исследования сразу, «все серии» — это
  // серии обоих. Без этого отбора врач открывал бы сегодняшнюю КТ в раскладке
  // 2×2 и получал в соседних окнах прошлогоднюю МРТ — молча, без подписи, и
  // принял бы её за часть текущего исследования. В динамике такая путаница
  // опаснее пустого окна.
  const своё = getStudyOfSeries(seriesUID);
  const others = _getAllSeries()
    .filter(s => s && s.seriesUID)
    .filter(s => !своё || (getStudyOfSeries(s.seriesUID) || s.studyUID) === своё)
    .map(s => ({ seriesUID: s.seriesUID, count: (s.images || s.files || []).length }));

  const plan = planStackFill({
    vpIds,
    activeId: _currentViewportId,
    seriesUID,
    others,
    occupied: [..._vpSeries.keys()],
  });

  for (const step of plan) {
    let ids = step.seriesUID === seriesUID ? imageIds : _getSeriesImageIds(step.seriesUID);
    // Серия числится, а кадров по ней нет (битый разбор, отменённая загрузка) —
    // ставим текущую: чёрное окно хуже повторённого снимка.
    if (!ids?.length) ids = imageIds;
    if (!ids?.length) continue;
    try {
      await loadStack(step.vpId, ids);
      _vpSeries.set(step.vpId, step.seriesUID);
      if (step.active) _currentViewportId = step.vpId;
    } catch (e) {
      log.warn(`[UI] окно ${step.vpId}: серия не открылась —`, e.message);
    }
  }

  if (vpIds.length > 1) {
    log.info(`[UI] раскладка ${_currentLayout}: заполнено окон ${_vpSeries.size}/${vpIds.length}`);
  }
}

// ══════════════════════════════════════════════════════════════
// [10.0.4] Строить объём заранее — по желанию
// ══════════════════════════════════════════════════════════════

const PREBUILD_KEY = 'mikhmel.prebuildVolume';

/** Включено ли построение объёма сразу после загрузки серии. */
export function isVolumePrebuildEnabled() {
  try { return localStorage.getItem(PREBUILD_KEY) === '1'; } catch (e) { return false; }
}

/** Переключить и сказать, что стало. */
export function toggleVolumePrebuild() {
  const next = !isVolumePrebuildEnabled();
  try { localStorage.setItem(PREBUILD_KEY, next ? '1' : '0'); } catch (e) { /* */ }
  showToast(next
    ? 'Объём будет строиться сразу после загрузки. Реформаты откроются мгновенно, но памяти уйдёт вдвое больше.'
    : 'Объём строится только когда открываете проекцию. Памяти экономнее.',
    'info', 5000);
  _syncPrebuildButton();
  return next;
}

function _syncPrebuildButton() {
  const b = document.getElementById('btn-prebuild-volume');
  if (!b) return;
  const on = isVolumePrebuildEnabled();
  b.classList.toggle('active', on);
  const label = b.querySelector('.prebuild-label');
  if (label) label.textContent = on ? 'Объём сразу: вкл' : 'Объём сразу: выкл';
}

async function loadSeriesIntoViewport(seriesUID) {
  if (_loadingLock) { log.debug('[UI] loadSeries skipped — already loading'); return; }
  _loadingLock = true;
  // [Занавес] Опускается с самого клика по серии — вспышке негде мигнуть
  const _vc = document.getElementById('viewport-container');
  _vc?.classList.add('vol-warming');
  log.info('[Занавес] опущен — загрузка серии');
  setTimeout(() => {
    if (_vc?.classList.contains('vol-warming')) { _vc.classList.remove('vol-warming'); log.warn('[Занавес] снят страховкой (20с)'); }
  }, 20000);
  try {
  // [FIX-6] Универсальный геттер — работает с обоими путями
  let imageIds = _getSeriesImageIds(seriesUID);
  if (!imageIds.length) { _loadingLock = false; return; }

  // [10.3.0] Киносъёмку в реформаты не пускаем — их из неё не построить.
  //
  // Живой случай: коронарография, 233 кадра, все сняты С ОДНОГО положения
  // трубки. Программа честно уходила в MPR, строила «объём» нулевой толщины и
  // писала на экран «Реформат: Infinity/Infinity» — деление на нулевой размах
  // положений. Три окна из четырёх при этом чёрные.
  //
  // Кино нужно крутить, а не резать. Переводим в одиночное окно и говорим
  // почему — одним понятным предложением, а не молча.
  const _serCine = _getAllSeries().find(s => s.seriesUID === seriesUID);
  if (_serCine?.isCine && isMprLayout(_currentLayout)) {
    log.info(`[UI] серия — киносъёмка (${_serCine.cineWhy}), реформаты не строим`);
    try {
      showToast('Это киносъёмка: кадры сняты с одного положения, поэтому реформаты из них не строятся. Открываю как кино.', 'info', 5000);
      _vc?.classList.remove('vol-warming');
      _loadingLock = false;
      await changeLayout('1x1');
      await activateViewMode('2d');
      return await loadSeriesIntoViewport(seriesUID);
    } catch (e) {
      log.warn('[UI] переход в одиночное окно для кино не удался:', e.message);
    }
  }

  // В MPR режиме нужно минимум 10 срезов — автовыбор серии с наибольшим числом изображений
  // [10.0.2] mpr6 строит тот же объём: без этой проверки короткая серия
  // (локалайзер, скаут) уходила бы в реформат и давала шесть чёрных окон,
  // причём молча — предупреждение показывалось только для mpr.
  if (isMprLayout(_currentLayout) && imageIds.length < 10) {
    const allSeries = _getAllSeries();
    const best = allSeries.slice().sort((a, b) => (b.images || b.files || []).length - (a.images || a.files || []).length)[0];
    if (best && (best.images || best.files || []).length >= 10) {
      seriesUID = best.seriesUID;
      imageIds = _getSeriesImageIds(seriesUID);
      log.debug(`[UI] MPR auto-select: ${seriesUID} (${imageIds.length} images)`);
    } else {
      log.warn('[UI] No series with 10+ images for MPR');
      showToast('Для реформатов нужна серия от 10 срезов — в исследовании такой нет', 'warning', 3500);
      // Занавес опущен с клика по серии; на этом выходе его снимала только
      // 20-секундная страховка, и экран всё это время оставался чёрным.
      _vc?.classList.remove('vol-warming');
      return;
    }
  }

  // [11.1.72] СМЕНА СЕРИИ — СМЕНА ХОЛСТА.
  //
  // Владелец: «я не могу открыть серию 808 первой, первой открывается
  // максимально качественная серия». Верно: серию выбирает fileOps по
  // наибольшему числу срезов (там же и пояснение [6.4.0]), и 818 больше 808.
  // Мой совет «откройте нужную первой» был бессмысленным — этого нельзя
  // сделать.
  //
  // А беда была в том, что холст маски привязывался к первой открытой серии и
  // при переключении НЕ ПЕРЕСМАТРИВАЛСЯ: проверка принадлежности
  // (toolManager.#холстПодходитТому) зовётся только при ПЕРЕСОЗДАНИИ окон, а
  // loadSeriesIntoViewport окна не пересоздаёт — он меняет в них том. Отсюда и
  // «в открытом исследовании 512×512×818» при открытой серии 808.
  //
  // Лечим там, где серия и меняется. Прежняя разметка остаётся в памяти и в
  // выгруженных файлах; активной перестаёт быть только привязка к чужой сетке.
  if (_currentSeries && _currentSeries !== seriesUID) {
    try {
      const { сброситьХолстПодНовуюСерию } = await import('../tools/toolManager.js');
      const сброшен = сброситьХолстПодНовуюСерию();
      if (сброшен) log.info('[UI] серия сменилась — холст маски отвязан от прежней');
    } catch (e) { log.warn('[UI] холст под новую серию не отвязался:', e.message); }
  }

  _currentSeries = seriesUID;
  _currentImageIds = imageIds;

  // Progress callback for loading overlay
  const onProgress = (phase, pct, detail) => {
    updateLoadingOverlay(pct, phase === 'done' ? '✅ Загрузка завершена' : 'Загрузка объёма', detail);
  };

  if (_currentMode !== '2d' && _currentMode !== undefined) {
    const volumeId = `volume_${seriesUID}`;
    _currentVolumeId = volumeId;

    const step = _sliceStep || 1;
    const total = imageIds.length;
    const effective = step > 1 ? Math.ceil(total / step) : total;
    showLoadingOverlay(`Загрузка ${effective} срезов` + (step > 1 ? ` (каждый ${step}-й из ${total})` : ''), '', 0);

    // [10.0.2] mpr6 идёт по той же ветке, что mpr: обе раскладки строят
    // объём и заполняют проекции, отличаются только числом окон
    if (isMprLayout(_currentLayout)) {
      const allIds = getViewportIds();
      const vpIds = allIds.filter(id => id !== 'volume3d' && id !== 'view-3d' && id !== 'native');
      // [12.0.10] Нативное окно СЛЕДУЕТ ЗА ВЫБРАННОЙ СЕРИЕЙ. «Нативные срезы»
      // есть у каждой серии — это её собственные кадры, как их отдал аппарат,
      // без реконструкции. Прежнее правило [NATIVE 6.0.0] (самая тонкосрезная
      // ORIGINAL-серия исследования, независимая от выбора) путало владельца:
      // врач выбирает Body Venous, а в нативном окне лежит другая серия, и
      // смена серии окно «не меняет». Кадры серии кладём ЦЕЛИКОМ, без
      // sliceStep-прореживания: полный счёт первичных срезов. Выпадающий
      // список в углу окна остаётся ручным выбором до следующей смены серии.
      if (allIds.includes('native')) {
        try { await loadStack('native', imageIds); updateNativeLabel(imageIds); }
        catch (e) { log.warn('[NATIVE] stack load:', e.message); }
        _renderNativeSeriesSelect(seriesUID);
      }
      await loadVolume(volumeId, imageIds, vpIds, { sliceStep: step, onProgress });
      // [10.0.2] Вместо четырёх ре-рендеров вслепую — одно ожидание размера
      // с пересчётом камер.
      //
      // Было: [100, 500, 1200, 2500].forEach(d => setTimeout(_forceRerender, d)),
      // где _forceRerender звал re.resize(true, true). Второй аргумент — это
      // keepCamera, и движок при нём восстанавливает ПРЕЖНЮЮ камеру поверх
      // только что правильно посчитанной. А прежняя как раз и была вырожденной:
      // мобильная вёрстка не успевала выдать вьюпортам ненулевой размер к
      // моменту setVolumesForViewports. Каждая из четырёх попыток исправно
      // возвращала поломку на место — MPR оставался чёрным, пока серию не
      // загрузят второй раз.
      //
      // ensureViewportsReady дожидается фактического размера (ResizeObserver
      // плюс кадры анимации, потолок 3 с) и делает resize(true, false) с
      // resetCamera на каждом окне. Подробности — в самой функции.
      //
      // [10.0.2] Передаём ВСЕ окна раскладки, а не только срезовые: в mpr6
      // есть ещё два объёмных и нативное. resize и так engine-wide, но ждать
      // размера и перерисовывать надо тоже все, иначе объёмные останутся
      // чёрными ровно по той же причине, по которой чернел MPR.
      const _vpReady = ensureViewportsReady(allIds, { timeoutMs: 3000 })
        .then(({ sized, waitedMs }) => {
          if (!sized) log.warn(`[UI] MPR: размер вьюпортов не устоялся за ${waitedMs} мс`);
        })
        .catch(e => log.warn('[UI] ensureViewportsReady:', e.message));

      activateDataTools();
      // [MPR-SYNC] Синхронизация W/L между MPR viewports
      enableMPRSync();
    } else {
      // [FIX-MOB] switchViewMode теперь async — ждём чтобы viewport был готов
      _currentViewportId = await switchViewMode('viewport-container', _currentMode, imageIds, volumeId);
      setTimeout(() => activateDataTools(), 500);
    }
    hideLoadingOverlay();
  } else {
    // [10.0.6] Идентификатор тома от ПРЕЖНЕЙ серии не должен пережить смену
    // серии. Он оставался как был, и всё, что берёт «текущий объём» —
    // клинические кнопки (BMD, кальций), правило planViewModeVolume, —
    // молча работало с данными предыдущего исследования.
    if (_currentVolumeId
        && String(_currentVolumeId).replace(/^cornerstoneStreamingImageVolume:/, '') !== `volume_${seriesUID}`) {
      _currentVolumeId = null;
    }
    await _fillStackViewports(seriesUID, imageIds);
    requestAnimationFrame(() => requestAnimationFrame(() => { _vc?.classList.remove('vol-warming'); log.info('[Занавес] снят (стопка)'); }));
    activateDataTools();

    // [10.0.4] По желанию — построить объём заранее, фоном.
    //
    // По умолчанию выключено, и вот почему. Объём это ВТОРАЯ копия всех
    // пикселей: серия из 800 срезов 512×512 занимает под 400 МБ сверх кадров,
    // уже лежащих в кэше. На телефоне это прямой путь к тому, что браузер
    // выгрузит вкладку. Пока объём не нужен — не строим.
    //
    // Кому реформаты нужны всегда, включает переключатель в «Ещё», и тогда
    // ожидание переезжает с нажатия на проекцию в фоновое время сразу после
    // загрузки. Кадры к этому моменту уже в кэше, так что сборка идёт быстро.
    if (isVolumePrebuildEnabled()) {
      const volumeId = `volume_${seriesUID}`;
      _currentVolumeId = volumeId;
      ensureVolumeReady(volumeId, imageIds, {
        onProgress: (pct) => { if (pct >= 100) log.info('[Объём] построен заранее'); },
      }).catch(e => log.debug('[Объём] заранее:', e.message));
    }
  }
  // [10.0.7] Смена серии — момент, когда тома ПРЕЖНИХ серий уже никому не
  // видны, но продолжали лежать в кэше до самой нехватки памяти. Отдаём их
  // сразу: реестр помнит, из чего каждый собран, и вернёт при надобности.
  // Том текущей серии под защитой — включая собираемый фоном «Объём сразу».
  try { memoryGuardian.reclaim([`volume_${seriesUID}`]).catch(() => {}); } catch (e) { /* */ }
  updateOverlays(seriesUID);
  highlightActiveSeries(seriesUID); // [6.4.1] подсветить активную серию в панели
  // [6.0.3] Гарантированная перерисовка шкал/сетки после любой загрузки серии
  setTimeout(refreshGridOverlays, 150);
  renderDICOMTags(); // [FIX] Заполняем DICOM теги

  // [FIX] Один вызов getAllSeries вместо двух (autoSelectTool + autoPresetByModality)
  const allSeriesCached = _getAllSeries();
  autoSelectTool(seriesUID, allSeriesCached);
  autoPresetByModality(seriesUID, allSeriesCached);

  // [NEW] Восстановить сохранённые аннотации для этой серии
  autoLoadAnnotations(seriesUID).then(restored => {
    if (restored) showToast('📌 Аннотации восстановлены', 'info', 1500);
    emit('seriesList:updated');
  });

  // [11.1.4] Автопереключение профиля по модальности — ЗДЕСЬ его вызов.
  //
  // До этой версии галочка «Переключать профиль автоматически» только хранила
  // флаг: `detectProfileForModality` не вызывалась ниоткуда, и владелец
  // справедливо сообщил, что открывает МРТ головного мозга, а профиль
  // остаётся общим. Функция была, кнопка была — не было вот этой строки.
  try {
    const серия = (allSeriesCached || []).find((s) => s.seriesUID === seriesUID);
    const модальность = серия?.modality;
    if (модальность) {
      import('./profileManager.js')
        .then((м) => м.автопрофильПоМодальности?.(модальность))
        .catch((e) => log.debug('[Профиль] авто:', e.message));
    }
  } catch (e) { log.debug('[Профиль] авто:', e.message); }

  // [NEW] Генерируем thumbnail для списка серий
  generateThumbnailForCurrent(seriesUID, _currentViewportId);
  } finally { _loadingLock = false; }
}






// ── VRT Preset Manager ──


// ══════════════════════════════════════════════════════════════
// Background Sync API
// ══════════════════════════════════════════════════════════════

export function queueMeasurementsSync(endpoint = null) {
  const sw = navigator.serviceWorker?.controller;
  if (!sw) { log.warn('[Sync] SW not available'); return; }
  const measurements = getAnnotations();
  if (!measurements?.length) { showToast('⚠️ Нет измерений', 'warning'); return; }
  sw.postMessage({
    type: 'QUEUE_MEASUREMENTS',
    payload: { measurements, timestamp: new Date().toISOString() },
    endpoint,
  });
  showToast('📡 Измерения поставлены в очередь синхронизации', 'info', 3000);
}

export function queueAnnotationsSync() {
  const sw = navigator.serviceWorker?.controller;
  if (!sw) return;
  sw.postMessage({
    type: 'QUEUE_ANNOTATIONS',
    payload: { annotations: exportAnnotations(), timestamp: new Date().toISOString() },
  });
  showToast('📡 Аннотации поставлены в очередь синхронизации', 'info', 3000);
}

// Слушаем ответы от SW
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const { type } = event.data || {};
    if (type === 'CACHES_CLEARED') showToast('🗑️ Кеш очищен', 'success');
    // [10.3.2] Размер кэша SW приходит САМ при запуске, никто его не просил.
    // Это было первое, что видел человек, открыв программу: «💾 Кеш SW: 0.0 MB».
    if (type === 'CACHE_SIZE') {
      try { console.debug('[SW] размер кэша, МБ:', (event.data.size / 1024 / 1024).toFixed(1)); } catch (e) { /* */ }
    }
  });
}









// ============================================================
// SEGMENTATION
// ============================================================
// ── ПУНКТ 9: Annotation Undo/Redo System ──
const UNDO_MAX = 50;

// #11: structuredClone вместо JSON.parse(JSON.stringify) — быстрее для сложных ROI








// Экспортируем для доступа из мобильного интерфейса
// [10.0.2] handleFiles добавлен в публичное API — без него не работала кнопка
// восстановления сеанса.
//
// sessionRestore._reopen() достаёт файлы исследования из IndexedDB и ищет,
// кому их отдать: сперва MikhmelViewer.handleFiles, потом
// MikhmelViewer.loadDICOMFiles. Ни одно из этих имён нигде не регистрировалось,
// а MikhmelViewer — Proxy, который на незнакомое свойство возвращает undefined.
// Значит _reopen ВСЕГДА доходил до `throw new Error('некому передать файлы')`,
// исключение ловилось тут же, и кнопка молча меняла подпись на
// «Не вышло — повторить». Со стороны это выглядело как мёртвая кнопка.
//
// handleFiles уже импортирован из ./fileOps.js и передаётся в initStudyTools —
// но туда аргументом, а не в глобальное API. Достаточно зарегистрировать.
// [10.0.2] toggleMaximizeViewport — из мобильного двойного касания:
// жест живёт в mobileNav, а разворот окна в layoutManager, и напрямую
// импортировать одно из другого нельзя без круга.
registerAPIs({ openFolderPicker, showLoadingOverlay, updateLoadingOverlay, hideLoadingOverlay, undoAnnotation, redoAnnotation, setActiveTool, activateViewMode, showPreset3DFullscreen, relayoutViewports, handleFiles, toggleMaximizeViewport,
  isVolumePrebuildEnabled, toggleVolumePrebuild,
  // [10.8.0] Совмещению нужно открыть опорную серию в реформатах, а
  // импортировать uiManager оттуда нельзя — вышло бы кольцо зависимостей.
  loadSeriesIntoViewport, changeLayout,
  // [11.1.88] Кто вернёт снимок, тот должен знать, ЧТО открыто. Владелец потерял
  // изображение при разметке (страж памяти сбросил том), и вернуть его было
  // нечем: панель инструментов маски не знает про состояние окон, а второй путь
  // к «какая серия открыта» заводить нельзя — он разойдётся с показанным.
  getCurrentSeries: () => _currentSeries,
  // [12.0.0] Ручка для возврата контекстных подсказок — см. пояснение в
  // contextUI.js. По умолчанию они отключены: врачу незачем сообщать, что он
  // смотрит грудную клетку.
  контекстныеПодсказки });

// [10.0.4] Переключатель «строить объём сразу» — в ящике «Ещё».
try {
  document.getElementById('btn-prebuild-volume')?.addEventListener('click', () => toggleVolumePrebuild());
  _syncPrebuildButton();
} catch (e) { /* разметки может не быть в тестовой среде */ }















// ============================================================
// CLINICAL EXTRAS — TAVI, ROI Hist, ROI W/L, Contrast Graph,
//                   Anonymize, Voice Control, WebGPU Check
// ============================================================
// Вставить в uiManager.js после bindSpecialModes()
// В initUI() добавить вызов: bindClinicalExtras();
// ============================================================










// ════════════════════════════════════════════════════════════════
// ПУНКТ 14: TAVI Interactive Angle Measurement
// ════════════════════════════════════════════════════════════════
let _taviOverlay = null;



// ════════════════════════════════════════════════════════════════
// ПУНКТ 16: Measurement Trend Tracking (IndexedDB)
// ════════════════════════════════════════════════════════════════
const MEAS_DB_NAME = 'MikhmelMeasurements';
const MEAS_DB_VERSION = 1;













// ============================================================
// DRAG & DROP on viewport
// ============================================================
function bindViewportDragDrop() {
  const vc = $('viewport-container');
  if (!vc) return;
  vc.addEventListener('dragover', (e) => { e.preventDefault(); vc.classList.add('drag-over'); });
  vc.addEventListener('dragleave', () => vc.classList.remove('drag-over'));
  vc.addEventListener('drop', async (e) => {
    e.preventDefault(); vc.classList.remove('drag-over');
    const files = [];
    for (const item of e.dataTransfer.items) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry?.isDirectory) await traverseDir(entry, files);
        else files.push(item.getAsFile());
      }
    }
    if (files.length) handleFiles(files);
  });
}

async function traverseDir(dirEntry, files) {
  return new Promise(resolve => {
    dirEntry.createReader().readEntries(async entries => {
      for (const entry of entries) {
        if (entry.isFile) { const f = await new Promise(r => entry.file(r)); files.push(f); }
        else if (entry.isDirectory) await traverseDir(entry, files);
      }
      resolve();
    });
  });
}

// ============================================================
// MOUSE TRACKING + VIEWPORT FOCUS on viewport
// ============================================================
function bindViewportMouseTracking() {
  const vc = $('viewport-container');
  if (!vc) return;
  vc.addEventListener('mousemove', (e) => {
    const pos = $('mouse-pos');
    if (pos) pos.textContent = `x:${e.offsetX} y:${e.offsetY} val:—`;
  });

  // ── ПУНКТ 2: Viewport Focus — клик/тач по viewport переключает _currentViewportId ──
  const focusViewport = (e) => {
    // Ищем ближайший viewport-элемент
    const vpEl = e.target.closest('[id^="viewport-"]');
    if (!vpEl) return;
    const vpId = vpEl.id.replace('viewport-', '');
    if (!vpId) return;

    // Проверяем, существует ли viewport в rendering engine
    const re = getRenderingEngine();
    const vp = re?.getViewport(vpId);
    if (!vp) return;

    // Устанавливаем активный viewport
    _currentViewportId = vpId;

    // Визуальная подсветка активного viewport
    vc.querySelectorAll(':scope > div').forEach(el => el.classList.remove('vp-focused'));
    vpEl.classList.add('vp-focused');

    // Обновляем overlay tool name
    const toolOverlay = $('overlay-tool');
    if (toolOverlay) {
      const activeTool = getActiveTool ? getActiveTool() : '';
      toolOverlay.textContent = activeTool || '';
    }

    log.debug(`[UI] Viewport focus: ${vpId}`);
  };
  vc.addEventListener('pointerdown', focusViewport);

  // Double-click maximize/restore viewport (desktop + mobile MPR)
  vc.addEventListener('dblclick', (e) => {
    const vpEl = e.target.closest('[id^="viewport-"]');
    if (!vpEl) return;
    toggleMaximizeViewport(vpEl);
  });

  // ─── [REFACTOR] Регистрация API через viewerAPI.js (вместо ручного window.MikhmelViewer) ───
  registerAPIs({
      setActiveTool: setActiveTool,
    getRenderingEngine,
    getViewportIds,
    getCurrentViewportId: () => _currentViewportId,
    _removeViewportsFromToolGroup: (reId, vpIds) => {
      try {
        const csTools = window.cornerstoneTools || window.MikhmelViewer?._csTools;
        if (csTools?.ToolGroupManager) {
          const tgs = csTools.ToolGroupManager.getAllToolGroups?.() || [];
          tgs.forEach(tg => {
            vpIds.forEach(vpId => {
              try { tg.removeViewports?.(reId, vpId); } catch (e) { log.debug('[UiManager]', e.message); }
            });
          });
        }
      } catch(e) { log.warn('[Cleanup] ToolGroup remove:', e.message); }
    },
    focusViewport: (vpId) => {
      _currentViewportId = vpId;
      vc.querySelectorAll(':scope > div').forEach(el => el.classList.remove('vp-focused'));
      const vpEl = document.getElementById(`viewport-${vpId}`);
      if (vpEl) vpEl.classList.add('vp-focused');
    },
    _getCinematicRenderer: getCinematicRenderer,
    // [10.19.17] Диагностика конвейера по проходам — в консоль, числами.
    // Запускается сама при негодном кадре; вручную: MikhmelViewer.диагностикаКонвейера()
    диагностикаКонвейера: (...а) => import('./cinematicControl.js').then((м) => м.диагностикаКонвейера(...а)),
    // [10.19.18] Глубина: что посчиталось и почему — числами.
    диагностикаГлубины: () => import('../rendering/depthExtractor.js').then((м) => м.getDepthDiagnostics()),
    // [11.1.77] СЧЁТ АННОТАЦИЙ ИЗНУТРИ ПРОГРАММЫ.
    //
    // Дважды я просил владельца спросить это из консоли через
    // window.cornerstoneTools — и дважды получил пустоту, потому что в
    // собранной программе этого имени НЕТ вовсе. Я его выдумал, не проверив,
    // и на основании пустого ответа снял свой же вывод про 737 298 записей.
    // Оба раза он потратил время впустую.
    //
    // Спрашивать надо ОТТУДА, где доступ настоящий — изнутри модуля, у того же
    // annotation.state, которым пользуется экспорт. Отсюда и эта дверь:
    //     MikhmelViewer.диагностикаАннотаций()
    диагностикаАннотаций: () => import('../tools/toolManager.js').then((м) => м.диагностикаАннотаций()),
    пересчитатьГлубину: () => import('../rendering/depthExtractor.js').then((м) => м.пересчитатьГлубину({ принудительно: true })),
    отладкаЛучаГлубины: (x, y) => import('../rendering/depthExtractor.js').then((м) => м.отладкаЛуча(x, y)),
    // [10.19.20] Отчёт WebGPU: адаптер, возможности, ошибки по проходам.
    диагностикаWebGPU: () => import('../rendering/webgpuDiag.js')
      .then((м) => м.диагностикаWebGPU({ рендерер: getCinematicRenderer() }))
      .then((итог) => { console.log(итог.текст); return итог; }),
    activateViewMode,
    showPreset3DFullscreen,
    getGPUReport,
    relayoutViewports,
    mobileMPRFocus,
    mobileMPRRestore,
  });
  emit('uiManager:ready');
}


// ============================================================
// DICOM TAGS RENDERING
// ============================================================
async function renderDICOMTags() {
  const sidebar = $('dicom-tags-content');
  const modal = $('modal-tags-content');
  if (!sidebar && !modal) return;

  if (!_currentImageIds || !_currentImageIds.length) {
    const placeholder = '<div style="padding:12px;text-align:center;color:var(--text-dim);font-size:11px;">Загрузите изображение</div>';
    if (sidebar) sidebar.innerHTML = placeholder;
    if (modal) modal.innerHTML = placeholder;
    return;
  }

  try {
    // [6.6.6] Берём РЕАЛЬНЫЙ файл серии (полные теги с адресами), а не reformat-
    // срез MPR (у него нет файла → уходило в meta-свалку). Fallback на текущий.
    let tagImageId = _currentImageIds[0];
    try {
      const realIds = _currentSeries ? _getSeriesImageIds(_currentSeries) : null;
      if (realIds && realIds.length) tagImageId = realIds[0];
    } catch (e) { /* оставляем текущий */ }
    const tags = await getDICOMTags(tagImageId);
    if (!tags || !Object.keys(tags).length) {
      const msg = '<div style="padding:12px;text-align:center;color:var(--text-dim);">Теги не найдены</div>';
      if (sidebar) sidebar.innerHTML = msg;
      if (modal) modal.innerHTML = msg;
      return;
    }

    // [6.6.5] Плоский список всех тегов (как RadiAnt/Horos): сортировка по
    // адресу, без деления на разделы. Дубликаты meta_* отсекаем, если адрес
    // уже показан из основного набора тегов.
    const TAG_NAMES = {
      '00100010': 'Patient Name', '00100020': 'Patient ID', '00100030': 'Birth Date', '00100040': 'Sex',
      '00080020': 'Study Date', '00080030': 'Study Time', '00081030': 'Study Description', '00080050': 'Accession',
      '0008103e': 'Series Description', '00200011': 'Series Number', '00080060': 'Modality', '00180015': 'Body Part',
      '00280010': 'Rows', '00280011': 'Columns', '00280030': 'Pixel Spacing', '00180050': 'Slice Thickness',
      '00280100': 'Bits Allocated', '00280101': 'Bits Stored', '00281050': 'Window Center', '00281051': 'Window Width',
      '00080070': 'Manufacturer', '00080080': 'Institution', '00081090': 'Station Name', '00181000': 'Device Serial',
    };

    // [6.6.9] Обратный словарь имя→адрес: превращаем meta_* ключи в настоящие
    // адреса и имена. info.name уже несёт camelCase-имя (StudyDate), по нему
    // и находим адрес. Строим карту один раз из TAG_NAMES + расширенного словаря.
    const NAME2ADDR = {};
    const NAME_FULL = { ...TAG_NAMES,
      '00080005': 'Specific Character Set', '00080008': 'Image Type', '00080016': 'SOP Class UID',
      '00080018': 'SOP Instance UID', '00080022': 'Acquisition Date', '00080023': 'Content Date',
      '00080032': 'Acquisition Time', '00080033': 'Content Time', '00080021': 'Series Date', '00080031': 'Series Time',
      '0020000d': 'Study Instance UID', '0020000e': 'Series Instance UID', '00200010': 'Study ID',
      '00200012': 'Acquisition Number', '00200052': 'Frame of Reference UID', '00201041': 'Slice Location',
      '00180020': 'Scanning Sequence', '00180021': 'Sequence Variant', '00180022': 'Scan Options',
      '00180023': 'MR Acquisition Type', '00180024': 'Sequence Name', '00180025': 'Angio Flag',
      '00180080': 'Repetition Time (TR)', '00180081': 'Echo Time (TE)', '00180082': 'Inversion Time (TI)',
      '00180083': 'Number of Averages', '00180084': 'Imaging Frequency', '00180085': 'Imaged Nucleus',
      '00180087': 'Magnetic Field Strength', '00180088': 'Spacing Between Slices', '00180091': 'Echo Train Length',
      '00180093': 'Percent Sampling', '00180094': 'Percent Phase FOV', '00180095': 'Pixel Bandwidth',
      '00181020': 'Software Versions', '00181030': 'Protocol Name', '00181250': 'Receive Coil Name',
      '00181314': 'Flip Angle', '00185100': 'Patient Position', '00101010': 'Patient Age', '00101030': 'Patient Weight',
      '00280002': 'Samples Per Pixel', '00280004': 'Photometric Interpretation', '00280102': 'High Bit',
      '00280103': 'Pixel Representation', '00281052': 'Rescale Intercept', '00281053': 'Rescale Slope',
      '00200032': 'Image Position', '00200037': 'Image Orientation', '00180050': 'Slice Thickness',
      '001021b0': 'Additional Patient History', '00200060': 'Laterality', '00201040': 'Position Reference',
      '00209057': 'In-Stack Position Number', '00209056': 'Stack ID', '00201002': 'Images In Acquisition',
      '00181088': 'Heart Rate', '00181090': 'Cardiac Number of Images', '00181094': 'Trigger Window',
      '00181080': 'Beat Rejection Flag',
      '00400254': 'Performed Procedure Step Description', '00400253': 'Performed Procedure Step ID',
      '00400244': 'Performed Procedure Step Start Date', '00400245': 'Performed Procedure Step Start Time',
      '00400242': 'Performed Station Name', '00400243': 'Performed Location',
    };
    // ключ нормализуем: убираем пробелы/скобки, нижний регистр → 'studydate'
    const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [addr, nm] of Object.entries(NAME_FULL)) NAME2ADDR[norm(nm)] = addr;
    // Ручные соответствия для camelCase-имён метаданных, где нормализация отличается
    const META_ALIAS = {
      studydate: '00080020', seriesdate: '00080021', acquisitiondate: '00080022', contentdate: '00080023',
      studytime: '00080030', seriestime: '00080031', acquisitiontime: '00080032', contenttime: '00080033',
      accessionnumber: '00080050', modality: '00080060', manufacturer: '00080070', institutionname: '00080080',
      stationname: '00081010', studydescription: '00081030', seriesdescription: '0008103e',
      manufacturermodelname: '00081090', patientid: '00100020', patientbirthdate: '00100030', patientsex: '00100040',
      patientage: '00101010', patientweight: '00101030', bodypartexamined: '00180015', mracquisitiontype: '00180023',
      angioflag: '00180025', slicethickness: '00180050', repetitiontime: '00180080', echotime: '00180081',
      inversiontime: '00180082', numberofaverages: '00180083', imagingfrequency: '00180084', imagednucleus: '00180085',
      magneticfieldstrength: '00180087', spacingbetweenslices: '00180088', echotrainlength: '00180091',
      percentsampling: '00180093', percentphasefieldofview: '00180094', pixelbandwidth: '00180095',
      deviceserialnumber: '00181000', softwareversions: '00181020', protocolname: '00181030',
      receivecoilname: '00181250', flipangle: '00181314', patientposition: '00185100',
      studyinstanceuid: '0020000d', seriesinstanceuid: '0020000e', studyid: '00200010', seriesnumber: '00200011',
      acquisitionnumber: '00200012', instancenumber: '00200013', frameofreferenceuid: '00200052',
      slicelocation: '00201041', imagepositionpatient: '00200032', imageorientationpatient: '00200037',
      samplesperpixel: '00280002', photometricinterpretation: '00280004', rows: '00280010', columns: '00280011',
      pixelspacing: '00280030', rowpixelspacing: '00280030', columnpixelspacing: '00280030',
      bitsallocated: '00280100', bitsstored: '00280101', highbit: '00280102', pixelrepresentation: '00280103',
      smallestimagepixelvalue: '00280106', largestimagepixelvalue: '00280107',
      sopclassuid: '00080016', sopinstanceuid: '00080018', reconstructiondiameter: '00181100',
      inplanephaseencodingdirection: '00181312', transfersyntaxuid: '00020010', sar: '00181316',
      variableflipangleflag: '00181315',
      // [6.7.0] Последний хвост — менее частые теги из вашего исследования
      additionalpatienthistory: '001021b0', laterality: '00200060',
      positionreferenceindicator: '00201040', instackpositionnumber: '00209057',
      stackid: '00209056', imagesinacquisition: '00201002',
      heartrate: '00181088', cardiacnumberofimages: '00181090', triggerwindow: '00181094',
      beatrejectionflag: '00181080',
      performedprocedurestepdescription: '00400254', performedprocedurestepid: '00400253',
      performedprocedurestepstartdate: '00400244', performedprocedurestepstarttime: '00400245',
      performedstationname: '00400242', performedlocation: '00400243',
    };

    const seenAddr = new Set();
    const rows = [];
    for (const [tag, info] of Object.entries(tags)) {
      let cleanTag = tag.replace(/^x/, '').toLowerCase();
      let displayName = info.name || TAG_NAMES[cleanTag] || null;
      // meta_* ключ → разрешаем в настоящий адрес по имени
      if (tag.startsWith('meta_')) {
        const key = tag.slice(5);                       // 'studydate'
        const nk = norm(info.name || key);              // нормализуем имя
        const resolved = META_ALIAS[key] || META_ALIAS[nk] || NAME2ADDR[nk];
        if (resolved) {
          cleanTag = resolved;
          displayName = NAME_FULL[resolved] || info.name || key;
        } else {
          displayName = info.name || key;               // редкий тег — оставляем имя
        }
      }
      const isHexAddr = /^[0-9a-f]{8}$/.test(cleanTag);
      if (isHexAddr) {
        if (seenAddr.has(cleanTag)) continue;           // дубликат по адресу
        seenAddr.add(cleanTag);
      }
      const name = displayName || cleanTag;
      const val = (info.value || '').toString().substring(0, 160);
      const addr = isHexAddr ? `${cleanTag.slice(0, 4)},${cleanTag.slice(4)}` : cleanTag;
      rows.push({ addr, sortKey: isHexAddr ? cleanTag : 'zzzz' + cleanTag, name, value: val, vr: info.vr || '' });
    }
    rows.sort((a, b) => a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0);

    let html = '';
    for (const t of rows) {
      html += `<div class="tag-row" style="display:flex;font-size:10px;padding:2px 6px;border-bottom:1px solid rgba(255,255,255,0.03);">` +
        `<span style="color:var(--text-dim);min-width:80px;">(${t.addr})</span>` +
        `<span style="color:var(--text-dim);min-width:28px;text-align:center;">${t.vr}</span>` +
        `<span style="color:var(--cyan,#67e8f9);min-width:150px;margin:0 4px;">${t.name}</span>` +
        `<span style="color:var(--text-primary);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${t.value}</span>` +
        `</div>`;
    }

    if (sidebar) sidebar.innerHTML = html;
    if (modal) modal.innerHTML = html;

    // Wire up modal search
    const modalSearch = $('modal-tag-search');
    if (modalSearch && !modalSearch._bound) {
      modalSearch._bound = true;
      modalSearch.addEventListener('input', () => {
        const q = modalSearch.value.toLowerCase();
        modal?.querySelectorAll('.tag-row')?.forEach(row => {
          row.style.display = row.textContent.toLowerCase().includes(q) ? '' : 'none';
        });
      });
    }

    emit('dicomTags:updated');
    // [FIX] Освобождаем память — оставляем только текущую серию
    if (_currentImageIds) trimDataSetCache(_currentImageIds);
  } catch (e) {
    log.warn('[UI] renderDICOMTags:', e.message);
    const errMsg = '<div style="padding:12px;text-align:center;color:#f87171;font-size:11px;">Ошибка чтения тегов</div>';
    if (sidebar) sidebar.innerHTML = errMsg;
    if (modal) modal.innerHTML = errMsg;
  }
}

// ============================================================
// LOADING OVERLAY
// ============================================================
// [FIX] Таймер для elapsed — один экземпляр, без утечек
let _loadElapsedTimer = null;
let _loadStartTime = 0;
let _cancelHandler = null;

function showLoadingOverlay(title, detail, pct) {
  const o = $('loading-overlay');
  if (!o) return;
  o.classList.add('active');

  // [FIX] Таймер elapsed — очищаем предыдущий перед созданием нового
  _loadStartTime = Date.now();
  if (_loadElapsedTimer) clearInterval(_loadElapsedTimer);
  _loadElapsedTimer = setInterval(() => {
    const el = $('loading-elapsed');
    if (!el || !o.classList.contains('active')) {
      clearInterval(_loadElapsedTimer);
      _loadElapsedTimer = null;
      return;
    }
    const sec = ((Date.now() - _loadStartTime) / 1000).toFixed(0);
    el.textContent = `⏱ ${sec}с`;
  }, 500);

  const t = $('loading-title'); if (t) t.textContent = title || '';
  const d = $('loading-detail'); if (d) d.textContent = detail || '';
  updateLoadingOverlay(pct || 0, title, detail);

  // [FIX] Cancel button — без cloneNode, просто removeEventListener + addEventListener
  const cb = $('loading-cancel');
  if (cb) {
    if (_cancelHandler) cb.removeEventListener('click', _cancelHandler);
    _cancelHandler = () => {
      cancelLoad();
      if (window._loadAbortController) {
        try { window._loadAbortController.abort(); } catch (e) { log.debug('[UiManager]', e.message); }
      }
      hideLoadingOverlay();
      showToast('⛔ Загрузка отменена', 'warning', 1500);
    };
    cb.addEventListener('click', _cancelHandler);
  }
}

function updateLoadingOverlay(pct, title, detail) {
  const p = $('loading-percent'); if (p) p.textContent = Math.round(pct) + '%';
  const b = $('loading-bar'); if (b) b.style.width = pct + '%';
  if (title) { const t = $('loading-title'); if (t) t.textContent = title; }
  if (detail) { const d = $('loading-detail'); if (d) d.textContent = detail; }
  // Also update thin progress bar
  const bar = $('progress-bar');
  if (bar) { bar.style.width = pct + '%'; bar.classList.toggle('hidden', pct <= 0); }
}

function hideLoadingOverlay() {
  const o = $('loading-overlay'); if (o) o.classList.remove('active');
  const bar = $('progress-bar'); if (bar) { bar.style.width = '0'; bar.classList.add('hidden'); }
  if (_loadElapsedTimer) { clearInterval(_loadElapsedTimer); _loadElapsedTimer = null; }
  const el = $('loading-elapsed'); if (el) el.textContent = '';
}

// ============================================================
// GPU STATUS BAR
// ============================================================
function updateGPUStatusBar() {
  const info = getGPUInfo();
  if (!info) return;
  const dot = $('gpu-dot');
  if (dot) { dot.className = `gpu-dot ${info.tier.label.toLowerCase()}`; }
  const tier = $('gpu-tier-bottom');
  if (tier) tier.textContent = `GPU: ${info.tier.label}`;
  const badge = $('webgpu-badge');
  if (badge) {
    badge.textContent = info.webgpu ? 'WebGPU ✓' : 'WebGL2';
    badge.style.color = info.webgpu ? 'var(--success)' : 'var(--warning)';
    badge.style.background = info.webgpu ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)';
  }
  // GPU tab details
  const setTxt = (id, txt) => { const el = $(id); if (el) el.textContent = txt; };
  setTxt('gpu-tier-label', info.tier.label);
  setTxt('gpu-renderer', info.renderer);
  setTxt('gpu-webgpu-status', info.webgpu ? '✅ Yes' : '❌ No');
  setTxt('gpu-webgl2-status', info.webgl2 ? '✅ Yes' : '❌ No');
  setTxt('gpu-max-texture', info.maxTextureSize + 'px');
  setTxt('gpu-cache-size', info.tier.cacheSize + ' MB');
  setTxt('cache-usage', `0 / ${info.tier.cacheSize} MB`);
}
// ══════════════════════════════════════════════════════════════
// MOBILE MPR — фокус на одном панеле по двойному тапу
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Workspace, Theme, Auto-backup
// ══════════════════════════════════════════════════════════════

const WS_LS_KEY = 'mikhmel_workspace';
let _autoBackupTimer = null;

/**
 * Save full workspace state (layout, mode, W/L, series, viewport positions)
 * @param {string} [name='default']
 */
export function saveWorkspace(name = 'default') {
  const state = {
    layout: _currentLayout, mode: _currentMode, series: _currentSeries,
    volumeId: _currentVolumeId, sliceStep: _sliceStep,
    viewportId: _currentViewportId, timestamp: Date.now(),
  };
  // Save W/L per viewport
  try {
    const re = getRenderingEngine();
    if (re) {
      state.viewportStates = {};
      for (const vp of re.getViewports()) {
        const props = vp.getProperties?.() || {};
        state.viewportStates[vp.id] = { voiRange: props.voiRange, invert: props.invert };
      }
    }
  } catch (e) { log.debug("[uiManager]", e.message); }
  const workspaces = _getWorkspaces();
  workspaces[name] = state;
  try { localStorage.setItem(WS_LS_KEY, JSON.stringify(workspaces)); } catch { /* non-critical */ }
  showToast(`💾 Рабочее пространство "${name}" сохранён`, 'success', 1500);
}

/**
 * Restore workspace
 * @param {string} [name='default']
 */
export async function restoreWorkspace(name = 'default') {
  try {
    const workspaces = _getWorkspaces();
    const state = workspaces[name];
    if (!state) { showToast(`❌ Рабочее пространство "${name}" не найден`, 'error'); return; }
    _sliceStep = state.sliceStep || 1;
    if (state.series) await loadSeriesIntoViewport(state.series);
    // Restore W/L
    if (state.viewportStates) {
      try {
        const re = getRenderingEngine();
        if (re) {
          for (const [vpId, vs] of Object.entries(state.viewportStates)) {
            const vp = re.getViewport(vpId);
            if (vp && vs.voiRange) { vp.setProperties({ voiRange: vs.voiRange, invert: vs.invert }); vp.render(); }
          }
        }
      } catch (e) { log.debug("[uiManager]", e.message); }
    }
    showToast(`📂 Рабочее пространство "${name}" восстановлен`, 'success', 1500);
  } catch (e) { log.warn(`[uiManager] restoreWorkspace failed:`, e.message);  }
}

/**
 * List saved workspaces
 * @returns {Array<{name:string, timestamp:number}>}
 */
export function listWorkspaces() {
  return Object.entries(_getWorkspaces()).map(([name, s]) => ({ name, timestamp: s.timestamp, series: s.series }));
}

/**
 * Delete workspace
 * @param {string} name
 */
export function deleteWorkspace(name) {
  const ws = _getWorkspaces();
  delete ws[name];
  try { localStorage.setItem(WS_LS_KEY, JSON.stringify(ws)); } catch { /* non-critical */ }
}

function _getWorkspaces() {
  try { return JSON.parse(localStorage.getItem(WS_LS_KEY) || '{}'); } catch { return {}; }
}

/**
 * Start auto-backup every N minutes
 * @param {number} [intervalMin=5]
 */
export function startAutoBackup(intervalMin = 5) {
  stopAutoBackup();
  _autoBackupTimer = setInterval(() => {
    if (_currentSeries) saveWorkspace('_autosave');
  }, intervalMin * 60000);
  showToast(`🔄 Авто-сохранение каждые ${intervalMin} мин`, 'info', 1500);
}

/**
 * Stop auto-backup
 */
export function stopAutoBackup() {
  if (_autoBackupTimer) { clearInterval(_autoBackupTimer); _autoBackupTimer = null; }
}

/**
 * Switch UI color theme
 * @param {'dark'|'light'|'midnight'|'sepia'} theme
 */
export function setTheme(theme) {
  const themes = {
    dark: { '--bg-main': '#0c0e16', '--text-primary': '#e2e8f0', '--accent': '#3b82f6' },
    light: { '--bg-main': '#f8fafc', '--text-primary': '#1e293b', '--accent': '#2563eb' },
    midnight: { '--bg-main': '#020617', '--text-primary': '#cbd5e1', '--accent': '#818cf8' },
    sepia: { '--bg-main': '#1c1a14', '--text-primary': '#d4c5a0', '--accent': '#b8860b' },
  };
  const vars = themes[theme];
  if (!vars) return;
  for (const [k, v] of Object.entries(vars)) document.documentElement.style.setProperty(k, v);
  document.body.dataset.theme = theme;
  showToast(`🎨 Тема: ${theme}`, 'info', 1200);
}

/** Get full UI state */
export function getUIState() {
  return {
    currentLayout: _currentLayout, currentMode: _currentMode, currentSeries: _currentSeries,
    currentViewportId: _currentViewportId, currentVolumeId: _currentVolumeId,
    sliceStep: _sliceStep, loadingLock: _loadingLock, imageCount: _currentImageIds?.length || 0,
    viewportIds: getViewportIds(), activeTool: getActiveTool?.() || null,
    workspaces: listWorkspaces().length, autoBackup: !!_autoBackupTimer,
    theme: document.body.dataset.theme || 'dark',
  };
}

export function forceRerenderAll() {
  try { const re = getRenderingEngine(); if (re) { re.resize(true, true); re.renderViewports(re.getViewports().map(v => v.id)); } } catch (e) { log.debug("[uiManager]", e.message); }
}
export function getSliceStep() { return _sliceStep; }
export function setSliceStepAPI(step) { _sliceStep = Math.max(1, Math.min(step, 10)); }
export function getLayoutInfo() { return { layout: _currentLayout, mode: _currentMode }; }
export function getVRTPresetMap() { return { ...VRT_PRESET_MAP }; }
/**
 * [10.1.3] Что программа выяснила про видеокарту — наружу, для диагностики.
 *
 * Настольная команда `desktop:diag` раньше печатала свои измерения и предел
 * буфера WebGPU. Предел буфера — это 2048 МБ почти на любой карте, и человек
 * читал его как «программа думает, что у меня 2 гигабайта». Теперь рядом
 * показывается вывод самой программы: класс железа, оценка видеопамяти,
 * бюджет кэша и предельное число срезов.
 */
export function getGPUReport() {
  const g = getGPUInfo() || {};
  return {
    класс: g.tier?.label || '—',
    видеопамятьМБ: g.vramEstimateMB ?? null,
    бюджетКэшаМБ: g.tier?.cacheSize ?? null,
    предельноСрезов: g.recommendedSlices ?? null,
    отрисовщик: g.renderer || '—',
    webgl2: !!g.webgl2,
    webgpu: !!g.webgpu,
    мобильный: !!g.isMobile,
  };
}

export function hasVolumeData() { return !!_getVolumeScalarData(); }
export function getCurrentSeriesUID() { return _currentSeries; }
export function exportSystemDiagnostics() {
  const d = { ui: getUIState(), gpu: getGPUInfo(), browser: { ua: navigator.userAgent, memory: navigator.deviceMemory, cores: navigator.hardwareConcurrency } };
  const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `diag_${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
}
export function getLoadingState() { return { active: document.getElementById('loading-overlay')?.classList.contains('active') || false, loadingLock: _loadingLock }; }

